import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { loadLifecycle } from "../einstellungen/data";
import { WEBSITE_URL } from "../../../app/nav";
import { toast } from "../../../components/Toast";
import { t } from "../../../lib/sprache";

/**
 * Empfehlungsprogramm auf der Übersicht (Vertrieb 03.11.2026, Inhaber: „auf der Webseite und im Portal als Banner, nicht per Mail“).
 * Nur für Admins zahlender Konten (Abo aktiv, nicht gekündigt, nicht nur lesbar): Konten im Test sollen einrichten, nicht empfehlen.
 * Der persönliche Link trägt die Kennung des Kontos als utm_campaign; site.js reicht sie bei „Kostenlos testen“ an die
 * Registrierung weiter, die Herkunft steht dann in der Meldung „Neues Konto“. Die Gratismonate verbucht der Vertrieb bei Stripe.
 * Bedingungen und Beispiele: apps/website/empfehlungsprogramm.html.
 */
export const BOOSTER_BIS = "2027-01-31";

/** Persönlicher Empfehlungslink: die ersten acht Zeichen der Konto-ID als Kennung (eindeutig genug, kein Name in der Adresse). */
export function empfehlungsLink(orgId: string): string {
  const kennung = orgId.replace(/-/g, "").slice(0, 8);
  return `${WEBSITE_URL}/empfehlungsprogramm?utm_source=empfehlung&utm_medium=referral&utm_campaign=${kennung}#empfohlen`;
}

export function EmpfehlungHinweis({ orgId, today }: { orgId: string; today: string }) {
  const life = useQuery({ queryKey: ["org_lifecycle", orgId], queryFn: () => loadLifecycle(api, orgId), staleTime: 60_000, enabled: api.mode !== "demo" });
  const l = life.data;
  if (api.mode === "demo" || !l || !l.is_chef || l.read_only || l.status !== "active" || l.subscription_status !== "active" || l.cancel_requested_at) return null;
  // Der Start-Booster endet von selbst: danach zeigt der Hinweis die normalen Gratismonate
  const booster = today.slice(0, 10) <= BOOSTER_BIS;
  const link = empfehlungsLink(orgId);
  const kopieren = () => void navigator.clipboard?.writeText(link)
    .then(() => toast(t("Empfehlungslink kopiert.", "Referral link copied.")), () => toast(t("Kopieren hat nicht geklappt.", "Copying did not work."), "alert"));
  return (
    <section className="notice stack tk-empfehlung" aria-labelledby="empfehlung-titel">
      <strong id="empfehlung-titel">
        {booster ? t("Empfiehl CreatorOffice: bis zu 4 Monate gratis für dich", "Recommend CreatorOffice: up to 4 months free for you")
          : t("Empfiehl CreatorOffice: bis zu 2 Monate gratis für dich", "Recommend CreatorOffice: up to 2 months free for you")}
      </strong>
      <p>
        {t("Für jede Agentur, die über deinen Link bucht, schenken wir dir Gratismonate: einen, sobald sie drei Monate bezahlt hat, zwei, wenn sie das Jahr bucht.",
          "For every agency that books through your link, we give you free months: one once it has paid for three months, two if it books a year.")}
        {booster ? t(" Bis zum 31. Januar zählt alles doppelt.", " Until 31 January everything counts double.") : ""}
        {t(" Die Agentur startet selbst mit einem Gratismonat.", " The agency itself starts with a free month.")}
      </p>
      <small>{t("Gratismonate gelten im kleineren der beiden Tarife und werden mit deinen nächsten Rechnungen verrechnet.", "Free months apply in the smaller of the two plans and are offset against your next invoices.")}</small>
      <div className="row">
        <button type="button" className="primary" onClick={kopieren}>{t("Empfehlungslink kopieren", "Copy referral link")}</button>
        <a className="btn" href={`${WEBSITE_URL}/empfehlungsprogramm`} target="_blank" rel="noreferrer">{t("So funktioniert es", "How it works")}</a>
      </div>
    </section>
  );
}
